<template>
  <v-dialog v-model="visible" scrollable persistent max-width="800" @keydown.esc="cancelForm()">
    <v-card class="vehicle-form-card">
      <v-card-title class="primary white--text py-4">
        <v-icon left color="white">{{ isNewRecord ? 'mdi-car-plus' : 'mdi-car-settings' }}</v-icon>
        {{ title }}
        <v-spacer></v-spacer>
        <v-btn icon dark @click="cancelForm()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-4">
        <v-form ref="vehiculoForm" v-model="isValid">
          <v-container fluid>
            <!-- Información Básica -->
            <v-row>
              <v-col cols="12">
                <div class="section-title">
                  <v-icon color="primary" left>mdi-car</v-icon>
                  <span>Información Básica</span>
                </div>
              </v-col>
              <v-col cols="12" md="6">
               <v-select v-model="fields.condition" :items="filteredConditionItems" :rules="validations.condition"
                  :error-messages="errors.condition" :disabled="loading || filteredConditionItems.length === 0"
                  item-text="text" item-value="value" label="Condición *" required outlined dense
                  prepend-inner-icon="mdi-tag"
                  :hint="filteredConditionItems.length === 0 ? 'Selecciona un dealer primero' : ''" persistent-hint>
                  <template v-slot:selection="{ item }">
                    <v-icon left small :color="item.color">{{ item.icon }}</v-icon>
                    {{ item.text }}
                  </template>
                  <template v-slot:item="{ item }">
                    <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                    <span>{{ item.text }}</span>
                  </template>
                </v-select>
              </v-col>
              <!-- Campo de dealer (solo para colaboradores) -->
              <v-col cols="12" md="6" v-if="hasPartnerDealers">
                <v-autocomplete
  v-model="selectedDealerId"
  :items="availableDealers"
  :loading="loadingDealers"
  :rules="validations.dealer_id"
  :error-messages="errors.dealer_id"
  :disabled="loading"
  item-text="name"
  item-value="id"
  label="Dealer *"
  prepend-inner-icon="mdi-store"
  outlined
  dense
  clearable
  hide-no-data
>
  <template v-slot:selection="{ item }">
    <div class="d-flex align-center">
      <v-avatar size="24" class="mr-2">
        <img v-if="item.imageUrl && item.imageUrl.url" :src="item.imageUrl.url" alt="Logo">
        <v-icon v-else small color="grey">mdi-store</v-icon>
      </v-avatar>
      {{ item.name }}
    </div>
  </template>
  <template v-slot:item="{ item }">
    <div class="d-flex align-center">
      <v-avatar size="32" class="mr-3">
        <img v-if="item.imageUrl && item.imageUrl.url" :src="item.imageUrl.url" alt="Logo">
        <v-icon v-else color="grey">mdi-store</v-icon>
      </v-avatar>
      <div>
        <div>{{ item.name }}</div>
        <div class="text-caption text--secondary">{{ item.city }}</div>
      </div>
    </div>
  </template>
</v-autocomplete>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="fields.order_number"
                  :rules="validations.order_number"
                  :error-messages="errors.order_number"
                  :disabled="loading"
                  label="Número de Orden"
                  prepend-inner-icon="mdi-order-numeric-ascending"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="fields.brand"
                  :rules="validations.brand"
                  :error-messages="errors.brand"
                  :disabled="loading"
                  label="Marca"
                  required
                  prepend-inner-icon="mdi-car-side"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="fields.model"
                  :rules="validations.model"
                  :error-messages="errors.model"
                  :disabled="loading"
                  label="Modelo"
                  required
                  prepend-inner-icon="mdi-car-info"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="fields.version"
                  :rules="validations.version"
                  :error-messages="errors.version"
                  :disabled="loading"
                  label="Versión"
                  required
                  prepend-inner-icon="mdi-label-variant"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="fields.color"
                  :rules="validations.color"
                  :error-messages="errors.color"
                  :disabled="loading"
                  label="Color"
                  required
                  prepend-inner-icon="mdi-palette"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-switch
                  v-model="fields.planning"
                  :disabled="loading"
                  label="Incluir en Planning"
                  color="primary"
                  inset
                  dense
                  hide-details
                  class="mt-1"
                ></v-switch>
              </v-col>
            </v-row>
            <!-- Especificaciones Técnicas -->
            <v-row class="mt-2">
              <v-col cols="12">
                <div class="section-title">
                  <v-icon color="primary" left>mdi-engine</v-icon>
                  <span>Especificaciones Técnicas</span>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="fields.fuel"
                  :items="fuelTypes"
                  :rules="validations.fuel"
                  :error-messages="errors.fuel"
                  :disabled="loading"
                  label="Combustible"
                  required
                  prepend-inner-icon="mdi-gas-station"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="fields.transmission"
                  :items="[
                    { text: 'Manual', value: 'manual' },
                    { text: 'Automática', value: 'auto'}
                  ]"
                  :rules="validations.transmission"
                  :error-messages="errors.transmission"
                  :disabled="loading"
                  item-text="text"
                  item-value="value"
                  label="Transmisión"
                  required
                  prepend-inner-icon="mdi-transmission-tower"
                  outlined
                  dense
                >
                </v-select>
              </v-col>
              <v-col cols="12" v-if="HAS_PERMISSION('asignar_todo')">
                <v-select
                  v-model="selectedTodos"
                  :items="availableTodos"
                  :loading="loadingTodos"
                  :rules="validations.todos"
                  :error-messages="errors.todos"
                  :disabled="loading"
                  item-text="name"
                  item-value="id"
                  label="TO-DO"
                  prepend-inner-icon="mdi-format-list-checks"
                  outlined
                  dense
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                  hint="Selecciona las tareas asociadas al vehículo"
                  persistent-hint
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                      v-if="index < 3"
                      :color="item.button_color"
                      :text-color="item.text_color"
                      small
                      close
                      @click:close="removeTodo(item)"
                    >
                      <v-avatar left :color="item.button_color" class="darken-1" style="display: flex; align-items: center; justify-content: center;">
                        <span :style="{ color: item.text_color }" style="font-size: 12px; font-weight: bold;">{{ getInitial(item.name) }}</span>
                      </v-avatar>
                      {{ item.name }}
                    </v-chip>
                    <span
                      v-if="index === 3"
                      class="grey--text text-caption"
                    >
                      (+{{ selectedTodos.length - 3 }} más)
                    </span>
                  </template>
                  
                  <template v-slot:item="{ item }">
                    <v-list-item-avatar :color="item.button_color" style="display: flex; align-items: center; justify-content: center;">
                      <span :style="{ color: item.text_color }" class="font-weight-bold" style="font-size: 14px;">
                        {{ getInitial(item.name) }}
                      </span>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-select>
              </v-col>
            </v-row>
            <!-- Identificación -->
            <v-row class="mt-2">
              <v-col cols="12">
                <div class="section-title">
                  <v-icon color="primary" left>mdi-card-account-details</v-icon>
                  <span>Identificación del Vehículo</span>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="fields.iic_number"
                  :rules="validations.iic_number"
                  :error-messages="errors.iic_number"
                  :disabled="loading"
                  label="Número IIC"
                  prepend-inner-icon="mdi-identifier"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="fields.plate_number"
                  :rules="validations.plate_number"
                  :error-messages="errors.plate_number"
                  :disabled="loading"
                  label="Número de Matrícula"
                  required
                  prepend-inner-icon="mdi-numeric"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <!-- Ficha técnica (delantera/trasera) -->
              <v-col cols="12" md="6" v-for="docInput in documentImageInputs" :key="docInput.field">
                <v-file-input
                  v-model="docFiles[docInput.field]"
                  :error-messages="errors[docInput.field]"
                  :disabled="loading"
                  :label="docInput.label"
                  :prepend-icon="docInput.icon"
                  accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                  outlined
                  dense
                  show-size
                  placeholder="Seleccione una imagen o PDF"
                  @change="onDocFileChange(docInput.field, $event)"
                ></v-file-input>
                <v-card outlined class="pa-2 mt-n2 mb-2" v-if="docPreviews[docInput.field]">
                  <div class="d-flex align-center">
                    <v-icon v-if="docPreviews[docInput.field].isPdf" size="40" color="red darken-1" class="mr-3">mdi-file-pdf-box</v-icon>
                    <img v-else :src="docPreviews[docInput.field].src" :alt="docInput.label"
                      style="width:64px;height:48px;object-fit:cover;border-radius:4px;" class="mr-3">
                    <div class="caption grey--text">{{ docInput.label }}</div>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="fields.policy_number"
                  :rules="validations.policy_number"
                  :error-messages="errors.policy_number"
                  :disabled="loading"
                  label="Número de póliza"
                  prepend-inner-icon="mdi-shield-car"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="fields.frame_number"
                  :rules="validations.frame_number"
                  :error-messages="errors.frame_number"
                  :disabled="loading"
                  label="Número de Bastidor (VIN)"
                  required
                  prepend-inner-icon="mdi-car-settings"
                  outlined
                  dense
                  hint="Código único que identifica al vehículo"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <!-- Nuevo campo: Ubicación previa -->
              <v-col cols="12" md="12">
                <v-text-field
                  v-model="fields.previous_location"
                  :rules="validations.previous_location"
                  :error-messages="errors.previous_location"
                  :disabled="loading || hasMovements"
                  label="Ubicación previa a 1ª recepción"
                  prepend-inner-icon="mdi-map-marker"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <!-- Información del Cliente -->
            <v-row class="mt-2">
              <v-col cols="12">
                <div class="section-title">
                  <v-icon color="primary" left>mdi-account</v-icon>
                  <span>Información del Cliente</span>
                </div>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="fields.customer_email"
                  :rules="validations.customer_email"
                  :error-messages="errors.customer_email"
                  :disabled="loading"
                  label="Email del Cliente"
                  prepend-inner-icon="mdi-email"
                  outlined
                  dense
                  type="email"
                  hint="Correo electrónico del cliente final"
                  persistent-hint
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          :disabled="loading"
          @click="cancelForm()"
          color="grey darken-1"
          text
          class="px-4"
        >
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-btn
          :loading="loading"
          :disabled="saveIsDisabled"
          @click="saveForm()"
          color="primary"
          class="px-4"
          depressed
        >
          <v-icon left>mdi-content-save</v-icon>
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// import NotificacionService from '../../services/NotificacionService'
import VehiculoService from '../../services/VehiculoService'
import LocationService from '../../services/LocationService'
import TodoCategoryService from '../../services/TodoCategoryService'
import { mapGetters } from 'vuex'
import DealerService from '../../services/DealerService'

export default {
  props: {
    value: {
      type: Object,
      default: () => ({})
    }
  },

  data: () => ({
    fuelTypes: [
      'Gasolina',
      'Diésel',
      'Híbrido',
      'Híbrido Enchufable',
      'Eléctrico'
    ],
    visible: true,
    loading: false,
    loadingDealers: false,
    loadingTodos: false,
    locations: [],
    availableDealers: [],
    availableTodos: [],
    selectedDealerId: null,
selectedDealer: null,
    selectedTodos: [],
    originalTodos: [],
    isValid: false,
    docFiles: {
      technical_sheet_front_image: null,
      technical_sheet_back_image: null,
      circulation_permit_front_image: null,
      circulation_permit_back_image: null
    },
    docPreviews: {
      technical_sheet_front_image: null,
      technical_sheet_back_image: null,
      circulation_permit_front_image: null,
      circulation_permit_back_image: null
    },
    errors: {
      condition: [],
      dealer_id: [],
      order_number: [],
      iic_number: [],
      brand: [],
      model: [],
      version: [],
      color: [],
      fuel: [],
      transmission: [],
      frame_number: [],
      plate_number: [],
      policy_number: [],
      technical_sheet_front_image: [],
      technical_sheet_back_image: [],
      circulation_permit_front_image: [],
      circulation_permit_back_image: [],
      price: [],
      todos: [],
      customer_email: []
    },
    fields: {
      condition: null,
      dealer_id: null,
      order_number: null,
      iic_number: null,
      brand: null,
      model: null,
      version: null,
      color: null,
      fuel: null,
      transmission: null,
      frame_number: null,
      plate_number: null,
      policy_number: null,
      price: null,
      status: 'pending',
      previous_location: null,
      customer_email: null,
      planning: false
    },
    dealer_name: ''
  }),

  computed: {
    ...mapGetters(['HAS_PERMISSION', 'PARTNER_DEALER_IDS']),
    isNewRecord () {
      return !this.fields.id
    },
    documentImageInputs () {
      return [
        { field: 'technical_sheet_front_image', label: 'Ficha técnica (parte delantera)', icon: 'mdi-camera-front' },
        { field: 'technical_sheet_back_image', label: 'Ficha técnica (parte trasera)', icon: 'mdi-camera-rear' },
        { field: 'circulation_permit_front_image', label: 'Permiso de circulación (parte delantera)', icon: 'mdi-camera-front' },
        { field: 'circulation_permit_back_image', label: 'Permiso de circulación (parte trasera)', icon: 'mdi-camera-rear' }
      ]
    },
    hasPartnerDealers() {
      // Mostrar selector de dealer si es colaborador o tiene dealers partner
      return this.$store.getters.HAS_ROLE(['colaborator']) || this.$store.getters.PARTNER_DEALER_IDS.length > 0
    },
    // Dealer activo: el seleccionado si hay partners, sino el propio
    activeDealer() {
  if (this.hasPartnerDealers && this.selectedDealerId) {
    return this.availableDealers.find(d => d.id === this.selectedDealerId) || null
  }
  return this.$store.getters.CURRENT_USER?.dealer || null
},

    // Tipos activos del dealer activo
    activeDealerTypes() {
      const dealer = this.activeDealer
      if (!dealer) return []
      const types = []
      if (dealer.vn) types.push('vn')
      if (dealer.vo) types.push('vo')
      if (dealer.taller) types.push('taller')
      return types
    },

    // Items de condición filtrados según dealer activo + permisos del user
    filteredConditionItems() {
      const typeToCondition = {
        'vn': { text: 'VN', value: 'new', icon: 'mdi-star-outline', color: 'success' },
        'vo': { text: 'VO', value: 'used', icon: 'mdi-car-sports', color: 'info' },
        'taller': { text: 'Taller', value: 'taller', icon: 'mdi-hammer-wrench', color: 'warning' }
      }

      return this.activeDealerTypes
        .filter(type => this.HAS_PERMISSION(type))
        .map(type => typeToCondition[type])
    },
    hasMovements() {
      // Verificar si el vehículo tiene movimientos
      return this.fields && 
            this.fields.movements && 
            Array.isArray(this.fields.movements) && 
            this.fields.movements.length > 0
    },
    title () {
      return this.isNewRecord ? 'Nuevo Vehículo' : 'Modificar Vehículo'
    },
    saveIsDisabled () {
      // Deshabilitar si status es 'registered' y frame_number está vacío
      if (this.fields.status === 'registered' && !this.fields.frame_number) {
        return true
      }
      return this.loading || !this.isValid
    },
    allRequiredFieldsComplete () {
      const baseRequiredFields = (
        this.fields.frame_number
      )

      // Si es colaborador, también requerir dealer_id
      if (this.$store.getters.HAS_ROLE(['colaborator'])) {
        return baseRequiredFields && this.fields.dealer_id
      }

      return baseRequiredFields
    },
    validations () {
      const baseValidations = {
        isValid: true,
        condition: [v => !!v || 'La condición es requerida'],
        order_number: [],
        iic_number: [],
        brand: [],
        model: [],
        version: [],
        color: [],
        fuel: [v => !!v || 'El combustible es requerido'],
        transmission: [],
        frame_number: [
          v => {
            if (this.fields.status === 'registered') {
              return !!v || 'El número de bastidor es obligatorio para vehículos recepcionados.'
            }
            return true
          }
        ],
        plate_number: [],
        policy_number: [],
        price: [],
        todos: [],
        previous_location: [],
        customer_email: [
          v => {
            if (!v) return true // El campo es opcional
            // Validación de formato de email
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return emailPattern.test(v) || 'Debe ingresar un email válido'
          }
        ]
      }

      // Si es colaborador, agregar validación para dealer_id
      if (this.$store.getters.HAS_ROLE(['colaborator'])) {
        baseValidations.dealer_id = [v => !!v || 'Debe seleccionar un dealer']
      } else {
        baseValidations.dealer_id = []
      }

      return baseValidations
    }
  },

  methods: {
    saveForm () {
      this.$refs.vehiculoForm.validate()
      if (this.isValid) {
        this.loading = true
        
        // Asignar dealer_id basado en el rol del usuario (seguro ante valores faltantes)
        // Priorizar selectedDealer (si el usuario lo seleccionó), luego fields.dealer_id existente, y por último el dealer del usuario actual.
        const currentUser = this.$store.getters['CURRENT_USER'] || null
        let dealerId = this.selectedDealerId || this.fields.dealer_id || currentUser?.dealer?.id || null
        this.fields.dealer_id = dealerId
        
        let vehiculo = Object.assign({}, this.fields)
        vehiculo.planning = this.fields.planning ? 1 : 0
        // No enviar los objetos de imagen que vienen del servidor
        delete vehiculo.technical_sheet_front_image
        delete vehiculo.technical_sheet_back_image
        delete vehiculo.circulation_permit_front_image
        delete vehiculo.circulation_permit_back_image
        // Verificar si todos los campos requeridos están completos
        if (this.allRequiredFieldsComplete && vehiculo.status === 'pending') {
          // Si todos los campos requeridos están completos, establecer el estado a "created"
          vehiculo.status = 'created'
        } else if (!this.allRequiredFieldsComplete && vehiculo.status === 'created') {
          // Si falta algún campo requerido, dejar el estado como "pending"
          vehiculo.status = 'pending'
        }
        this.$store.dispatch(this.isNewRecord ? 'ADD_VEHICULOS' : 'UPDATE_VEHICULOS', vehiculo).then(response => {
          let { error, data, status } = response
          if (error) {
            if (status === 422) {
              // mostrando los errores de validación en los campos correspondientes
              // Process regular validation errors
              if (data.data.name && data.data.message && data.data.message.includes('Has superado el máximo de vehículos permitidos, debes adquirir un plan de pago superior para poder añadir más vehículos')) {
                this.$toast.error(data.data.message, {
                  duration: 6000,
                  position: 'top-center'
                })
                // Close the form after showing the error
                setTimeout(() => {
                  this.cancelForm()
                }, 1000)
              } else if (Array.isArray(data)) {
                // Regular field validation errors
                data.forEach(({ field, message }) => {
                  if (this.errors[field]) {
                    this.errors[field].push(message)
                  }
                })
              }
              data.forEach(({ field, message }) => {
                if (this.errors[field]) {
                  this.errors[field].push(message)
                }
              })
            } else {
              // mostrando cualquier otro error al usuario en una notificación
              this.$toast.error('Ocurrió un error al intentar registrar o actualizar el vehículo')
            }
            this.loading = false
          } else {
            /* if (vehiculo.status === 'created') {
              NotificacionService.get().then(({ data }) => {
                data.data.forEach(n => {
                  if (n.vehicle && n.vehicle.status === 'pending' && n.type === 'vehicle' && n.vehicle.id === this.value.id) {
                    NotificacionService.read({ id: n.id })
                  }
                })
              })
            } */
            
            // Determinar el ID del vehículo
            const vehicleId = data.id || data.data?.id || vehiculo.id

            const finishSave = async () => {
              await this.uploadDocumentImages(vehicleId)
              this.$emit('save', { insert: this.isNewRecord, value: data })
              this.visible = false
              this.loading = false
            }

            // Después de guardar el vehículo, asignar los TO-DO
            this.assignTodosToVehicle(vehicleId).then(finishSave).catch(error => {
              console.error('Error al asignar TO-DO:', error)
              this.$toast.warning('Vehículo guardado pero hubo un error al asignar las tareas')
              finishSave()
            })
          }
        })
      }
    },
    async loadAvailableDealers() {
  try {
    this.loadingDealers = true
    const currentUser = this.$store.getters.CURRENT_USER
    let dealers = []

    // 1. Agregar el dealer propio si tiene permiso
    if (currentUser.dealer && this.$store.getters.HAS_PERMISSION('crear_vehiculo')) {
      dealers.push(currentUser.dealer)
    }

    // 2. Agregar dealers partners
    const partnerDealerIds = this.$store.getters.PARTNER_DEALER_IDS
    const partnerDealersWithPermission = partnerDealerIds.filter(dealerId => {
      const perms = this.$store.getters.GET_PARTNER_PERMISSIONS_FOR_DEALER(dealerId)
      return perms.includes('crear_vehiculo')
    })

    if (partnerDealersWithPermission.length > 0) {
      const responses = await Promise.all(
        partnerDealersWithPermission.map(id => DealerService.view({ id }))
      )
      responses.forEach(response => {
        const dealer = response.data.data
        console.log('Dealer partner cargado:', dealer.name, '| vn:', dealer.vn, '| vo:', dealer.vo, '| taller:', dealer.taller)
        if (!dealers.find(d => d.id === dealer.id)) {
          dealers.push(dealer)
        }
      })
    }

    // 3. Si es colaborador, agregar los dealers asociados (clientes del colaborador)
    if (this.$store.getters.HAS_ROLE(['colaborator']) && currentUser.dealer) {
      try {
        const colaboradorResponse = await DealerService.view({
          id: currentUser.dealer.id,
          expand: 'dealersAsociados'
        })
        
        const dealersAsociados = colaboradorResponse.data.data.dealersAsociados || []
        console.log('Dealers asociados al colaborador:', dealersAsociados.length)
        
        dealersAsociados.forEach(dealer => {
          console.log('Dealer asociado cargado:', dealer.name, '| vn:', dealer.vn, '| vo:', dealer.vo, '| taller:', dealer.taller)
          // Solo agregar si no está ya en la lista
          if (!dealers.find(d => d.id === dealer.id)) {
            dealers.push(dealer)
          }
        })
      } catch (error) {
        console.error('Error al cargar dealers asociados del colaborador:', error)
      }
    }

    console.log('Total de dealers disponibles:', dealers.length)
    // Usar $set para garantizar reactividad
    this.$set(this, 'availableDealers', dealers)
  } catch (error) {
    console.error('Error al cargar dealers disponibles:', error)
    this.$toast.error('Error al cargar la lista de dealers')
  } finally {
    this.loadingDealers = false
  }
},
    
    async assignTodosToVehicle(vehicleId) {
      if (!vehicleId) {
        console.log('No hay vehicleId, saliendo')
        return
      }
      
      // Obtener IDs de los TO-DO seleccionados
      const selectedTodoIds = this.selectedTodos.map(todo => todo.id || todo)
      const originalTodoIds = this.originalTodos.map(todo => todo.id || todo)
      
      // Si no hay cambios, no hacer nada
      if (JSON.stringify(selectedTodoIds.sort()) === JSON.stringify(originalTodoIds.sort())) {
        console.log('No hay cambios en los TO-DO, saliendo')
        return
      }
      
      try {
        // Usar assign-todos para reemplazar TODOS los to-dos
        const result = await VehiculoService.assignTodos(vehicleId, selectedTodoIds)
        console.log('Resultado assignTodos:', result)
      } catch (error) {
        console.error('Error al asignar TO-DO al vehículo:', error)
        throw error
      }
    },
    
    removeTodo(todo) {
      const index = this.selectedTodos.findIndex(t => (t.id || t) === (todo.id || todo))
      if (index > -1) {
        this.selectedTodos.splice(index, 1)
      }
    },

    onDocFileChange (field, file) {
      if (!file) {
        this.docPreviews[field] = null
        return
      }
      const isPdf = file.type === 'application/pdf' || (file.name || '').toLowerCase().endsWith('.pdf')
      this.docPreviews[field] = { src: isPdf ? null : URL.createObjectURL(file), isPdf }
    },

    async uploadDocumentImages (vehicleId) {
      if (!vehicleId) return

      const uploads = [
        { field: 'technical_sheet_front_image', param: '_technical_sheet_front_image', fn: VehiculoService.uploadTechnicalSheetFrontImage, label: 'ficha técnica (parte delantera)' },
        { field: 'technical_sheet_back_image', param: '_technical_sheet_back_image', fn: VehiculoService.uploadTechnicalSheetBackImage, label: 'ficha técnica (parte trasera)' },
        { field: 'circulation_permit_front_image', param: '_circulation_permit_front_image', fn: VehiculoService.uploadCirculationPermitFrontImage, label: 'permiso de circulación (parte delantera)' },
        { field: 'circulation_permit_back_image', param: '_circulation_permit_back_image', fn: VehiculoService.uploadCirculationPermitBackImage, label: 'permiso de circulación (parte trasera)' }
      ]

      for (const upload of uploads) {
        const file = this.docFiles[upload.field]
        if (!file) continue
        const formData = new FormData()
        formData.append(upload.param, file)
        try {
          await upload.fn(vehicleId, formData)
        } catch (error) {
          console.error(`Error al subir imagen de ${upload.label}:`, error)
          this.$toast.warning(`Vehículo guardado, pero hubo un problema al subir la imagen de ${upload.label}`)
        }
      }
    },

    getInitial(name) {
      if (!name || name.length === 0) return '?'
      return name.charAt(0).toUpperCase()
    },
    
    async loadAvailableTodos() {
      try {
        this.loadingTodos = true
        const response = await TodoCategoryService.get()
        
        if (response.data && response.data.data) {
          // Soporta respuesta paginada o directa
          if (Array.isArray(response.data.data.data)) {
            this.availableTodos = response.data.data.data
          } else if (Array.isArray(response.data.data)) {
            this.availableTodos = response.data.data
          }
        }
      } catch (error) {
        console.error('Error al cargar TO-DO:', error)
        this.$toast.error('Error al cargar las tareas disponibles')
      } finally {
        this.loadingTodos = false
      }
    },
    
    async loadVehicleTodos(vehicleId) {
      // Este método carga los TO-DO específicos del vehículo
      // Ya que vienen en el expand del view, este método es solo por si acaso
      try {
        const response = await VehiculoService.view({ id: vehicleId })
        if (response.data && response.data.data && response.data.data.todos) {
          this.selectedTodos = [...response.data.data.todos]
          this.originalTodos = [...response.data.data.todos]
        }
      } catch (error) {
        console.error('Error al cargar TO-DO del vehículo:', error)
      }
    },
    
    cancelForm () {
      this.$emit('cancel')
    }

    // async loadAvailableDealers () {
    //   if (!this.$store.getters.HAS_ROLE(['colaborator'])) {
    //     return // Solo cargar dealers si es colaborador
    //   }

    //   try {
    //     this.loadingDealers = true
        
    //     // Obtener el ID del colaborador (usuario actual)
    //     const colaboradorId = this.$store.getters['CURRENT_USER'].dealer.id
        
    //     // Obtener dealers asociados al colaborador
    //     const response = await DealerService.view({
    //       id: colaboradorId,
    //       expand: 'dealersAsociados'
    //     })
        
    //     // Filtrar solo dealers que NO sean colaboradores
    //     this.availableDealers = (response.data.data.dealersAsociados || []).filter(dealer => 
    //       !dealer.is_colaborador
    //     )
    //   } catch (error) {
    //     console.error('Error al cargar dealers disponibles:', error)
    //     this.$toast.error('Error al cargar la lista de dealers')
    //   } finally {
    //     this.loadingDealers = false
    //   }
    // }
  },

  async created () {
    LocationService.get({ 'per-page': 1000 }).then(({ data }) => {
      this.locations = data.data.map(location => ({
        id: location.id,
        name: location.name
      }))
    }).catch(error => {
      console.error('Error al obtener ubicaciones:', error)
    })

    // Cargar TO-DO disponibles
    await this.loadAvailableTodos()

    if (this.hasPartnerDealers) {
      await this.loadAvailableDealers()
    }
    
    if (this.$route.params.id) {
      this.loading = true
      const { data } = await VehiculoService.view({ id: this.$route.params.id })
      this.fields = Object.assign({}, data.data)
      this.fields.planning = !!data.data.planning

      // Previews de imágenes de documentación existentes
      Object.keys(this.docPreviews).forEach(field => {
        const image = data.data[field]
        this.docPreviews[field] = (image && image.url) ? { src: image.url, isPdf: /\.pdf(\?|#|$)/i.test(image.url) } : null
        this.docFiles[field] = null
      })
      
      // Si es colaborador y el vehículo tiene dealer_id, encontrarlo en la lista
      // if (this.$store.getters.HAS_ROLE(['colaborator']) && this.fields.dealer_id) {
      //   this.selectedDealer = this.availableDealers.find(dealer => dealer.id === this.fields.dealer_id)
      // }
      
      this.loading = false
    }
  },

watch: {
  value: {
    handler(newValue) {
      if (newValue) {
        this.fields = Object.assign({}, newValue)
        this.fields.planning = !!newValue.planning

        // Previews de imágenes de documentación existentes
        Object.keys(this.docPreviews).forEach(field => {
          const image = newValue[field]
          this.docPreviews[field] = (image && image.url) ? { src: image.url, isPdf: /\.pdf(\?|#|$)/i.test(image.url) } : null
          this.docFiles[field] = null
        })

        // Inicializar selectedDealerId con el dealer del vehículo al editar
        if (newValue.dealer_id) {
          this.selectedDealerId = newValue.dealer_id
        }

        // Cargar TO-DO del vehículo si existe
        if (newValue.todos && Array.isArray(newValue.todos)) {
          this.selectedTodos = [...newValue.todos]
          this.originalTodos = [...newValue.todos]
        } else if (newValue.id && this.availableTodos.length > 0) {
          this.loadVehicleTodos(newValue.id)
        }
      }
    },
    immediate: true
  },
  selectedDealerId(newId, oldId) {
  this.fields.dealer_id = newId || null

  // Solo limpiar condition si el usuario cambió el dealer manualmente
  // (no cuando se inicializa al cargar el vehículo para editar)
  if (newId && oldId !== undefined && oldId !== null) {
    const validValues = this.filteredConditionItems.map(i => i.value)
    if (this.fields.condition && !validValues.includes(this.fields.condition)) {
      this.fields.condition = null
    }
  } else if (!newId && oldId) {
    // Solo limpiar condition si el usuario quitó el dealer (no en inicialización)
    this.fields.condition = null
  }
}
}
}
</script>

<style scoped>
.vehicle-form-card {
  border-radius: 8px;
  overflow: hidden;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.87);
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.section-title .v-icon {
  margin-right: 8px;
}
</style>
